import React from 'react';
import PropTypes from 'prop-types';
import TabBar from '../../components/common/TabBar';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import { getDetailBreadcrumbTrail } from '../../services/navLinkService';
import './customer360.css';

export function WorkspaceHeader({ title, subtitle, nav }) {
  return (
    <div className="customer360-view__header">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <h4 className="customer360-view__title mb-0">{title}</h4>
          {subtitle ? <p className="customer360-muted small mb-0">{subtitle}</p> : null}
        </div>
        {nav || null}
      </div>
    </div>
  );
}

WorkspaceHeader.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  nav: PropTypes.node,
};

export function WorkspaceSection({ title, icon, children, bodyClassName }) {
  return (
    <div className="customer360-monitoring-card customer360-snapshot">
      <div className="customer360-snapshot__header">
        <div className="customer360-snapshot__title-block">
          <div className="customer360-snapshot__title-row">
            {icon ? (
              <span className="customer360-snapshot__icon" aria-hidden>
                <i className={`fa ${icon}`} />
              </span>
            ) : null}
            <h6 className="customer360-snapshot__title mb-0">{title}</h6>
          </div>
        </div>
      </div>
      <div className={bodyClassName || 'customer360-snapshot__stat-rows'}>{children}</div>
    </div>
  );
}

WorkspaceSection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  children: PropTypes.node,
  bodyClassName: PropTypes.string,
};

export function WorkspaceStat({ label, value, onClick, isActive, isStatic }) {
  const className = `customer360-snapshot__stat${isActive ? ' is-active' : ''}${
    isStatic ? ' customer360-snapshot__stat--static' : ''
  }`;
  const body = (
    <React.Fragment>
      <div className="customer360-snapshot__stat-label">{label}</div>
      <div className="customer360-snapshot__stat-value">
        {value == null || value === '' ? <span className="customer360-muted">—</span> : value}
      </div>
    </React.Fragment>
  );
  if (isStatic || !onClick) {
    return <div className={className}>{body}</div>;
  }
  return (
    <button type="button" className={className} onClick={onClick} aria-pressed={!!isActive}>
      {body}
    </button>
  );
}

WorkspaceStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  onClick: PropTypes.func,
  isActive: PropTypes.bool,
  isStatic: PropTypes.bool,
};

export function WorkspaceSummary({ title, icon, stats, columns }) {
  const gridClass =
    columns === 4 ? 'customer360-grid-4' : columns === 2 ? 'customer360-grid-3' : 'customer360-grid-3';
  return (
    <div className="customer360-monitoring-card customer360-snapshot mb-3">
      <div className="customer360-snapshot__header">
        <div className="customer360-snapshot__title-block">
          <div className="customer360-snapshot__title-row">
            <span className="customer360-snapshot__icon" aria-hidden>
              <i className={`fa ${icon || 'fa-th-large'}`} />
            </span>
            <h6 className="customer360-snapshot__title mb-0">{title}</h6>
          </div>
        </div>
      </div>
      <div className="customer360-snapshot__stat-rows">
        <div className={gridClass}>
          {(stats || []).map((stat) => (
            <WorkspaceStat
              key={stat.key || stat.label}
              label={stat.label}
              value={stat.value}
              onClick={stat.onClick}
              isActive={stat.isActive}
              isStatic={stat.isStatic}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

WorkspaceSummary.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  stats: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.number,
};

function decorateActionChild(child, idx) {
  if (!React.isValidElement(child)) return child;
  const existing = child.props.className || '';
  let className = existing;
  if (className.indexOf('ticket360-actions__btn') < 0) {
    className = `${className} ticket360-actions__btn`.trim();
  }
  if (className.indexOf('customer360-provision-actions__btn') < 0) {
    className = `${className} customer360-provision-actions__btn`.trim();
  }
  return React.cloneElement(child, {
    key: child.key || idx,
    className,
  });
}

export function WorkspaceActions({ children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;

  return (
    <div className="customer360-monitoring-card customer360-provision-actions-card mb-3 ticket360-actions">
      <div className="customer360-provision-actions-card__header">
        <h6 className="customer360-provision-actions-card__title mb-0">Actions</h6>
      </div>
      <div className="customer360-provision-actions-card__body">
        <div className="customer360-provision-actions ticket360-actions__pills">
          {items.map((child, idx) => decorateActionChild(child, idx))}
        </div>
      </div>
    </div>
  );
}

WorkspaceActions.propTypes = {
  children: PropTypes.node,
};

export function WorkspaceTabs({ tabs, activeTab, onChange, ariaLabel }) {
  return (
    <TabBar
      tabs={tabs}
      activeTab={activeTab}
      onChange={onChange}
      className="customer360-console"
      ariaLabel={ariaLabel || 'Workspace sections'}
    />
  );
}

WorkspaceTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  activeTab: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

export function WorkspaceChrome({
  title,
  subtitle,
  nav,
  summary,
  actions,
  tabs,
  activeTab,
  onTabChange,
  children,
  breadcrumbs,
  listLabel,
  listTo,
  recordLabel,
}) {
  const crumbItems =
    breadcrumbs ||
    (listLabel && listTo
      ? getDetailBreadcrumbTrail({
          listLabel,
          listTo,
          recordLabel: recordLabel || (typeof title === 'string' ? title : undefined),
        })
      : null);

  return (
    <div className="customer360-view customer360-console">
      {crumbItems && crumbItems.length ? <Breadcrumbs items={crumbItems} /> : null}
      <WorkspaceHeader title={title} subtitle={subtitle} nav={nav} />
      {summary}
      {actions}
      {tabs && tabs.length ? (
        <WorkspaceTabs tabs={tabs} activeTab={activeTab} onChange={onTabChange} />
      ) : null}
      <div
        className="customer360-tabpanel py-2"
        role="tabpanel"
        id={`tabpanel-${activeTab || 'main'}`}
        aria-labelledby={activeTab ? `tab-${activeTab}` : undefined}
      >
        {children}
      </div>
    </div>
  );
}

WorkspaceChrome.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  nav: PropTypes.node,
  summary: PropTypes.node,
  actions: PropTypes.node,
  tabs: PropTypes.array,
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  children: PropTypes.node,
  breadcrumbs: PropTypes.array,
  listLabel: PropTypes.string,
  listTo: PropTypes.string,
  recordLabel: PropTypes.string,
};

export function tabFromQuery(search, defaultTab, allowed) {
  let tab = defaultTab;
  try {
    if (typeof URLSearchParams !== 'undefined') {
      const params = new URLSearchParams(search || '');
      tab = params.get('tab') || defaultTab;
    } else {
      const match = String(search || '').match(/[?&]tab=([^&]+)/);
      if (match) tab = decodeURIComponent(match[1]);
    }
  } catch (_) {
    tab = defaultTab;
  }
  if (allowed && allowed.length && !allowed.some((t) => t.id === tab)) return defaultTab;
  return tab;
}
